import { useId } from "react";
import { CalendarDays } from "lucide-react";
import { useLanguage } from "../i18n";

export type DateFilterMode = "ALL" | "WEEK" | "MONTH" | "YEAR" | "CUSTOM";

export type DateFilterValue = {
  mode: DateFilterMode;
  from: string;
  to: string;
};

export const EMPTY_DATE_FILTER: DateFilterValue = { mode: "ALL", from: "", to: "" };

export function RecordDateFilter({ value, onChange }: { value: DateFilterValue; onChange: (value: DateFilterValue) => void }) {
  const { t } = useLanguage();
  const fromId = useId();
  const toId = useId();
  const periodId = useId();
  return <div className="record-date-filter">
    <CalendarDays aria-hidden="true" />
    <select aria-label={t("时间查询方式", "Time filter")} value={value.mode} onChange={(event) => {
      const mode = event.target.value as DateFilterMode;
      onChange({ mode, from: defaultPeriod(mode), to: "" });
    }}>
      <option value="ALL">{t("全部时间", "All time")}</option>
      <option value="WEEK">{t("按周查询", "By week")}</option>
      <option value="MONTH">{t("按月查询", "By month")}</option>
      <option value="YEAR">{t("按年查询", "By year")}</option>
      <option value="CUSTOM">{t("自定义时间段", "Custom range")}</option>
    </select>
    {value.mode === "WEEK" && <input id={periodId} className="record-period-input" aria-label={t("选择周", "Select week")} type="week" value={value.from} onChange={(event) => onChange({ ...value, from: event.target.value })} />}
    {value.mode === "MONTH" && <input id={periodId} className="record-period-input" aria-label={t("选择月份", "Select month")} type="month" value={value.from} onChange={(event) => onChange({ ...value, from: event.target.value })} />}
    {value.mode === "YEAR" && <input id={periodId} className="record-period-input" aria-label={t("选择年份", "Select year")} type="number" min="2000" max="2100" step="1" value={value.from} onChange={(event) => onChange({ ...value, from: event.target.value })} />}
    {value.mode === "CUSTOM" && <div className="record-date-range">
      <label htmlFor={fromId}>{t("开始", "From")}</label>
      <input id={fromId} type="date" value={value.from} max={value.to || undefined} onChange={(event) => onChange({ ...value, from: event.target.value })} />
      <span>—</span>
      <label htmlFor={toId}>{t("结束", "To")}</label>
      <input id={toId} type="date" value={value.to} min={value.from || undefined} onChange={(event) => onChange({ ...value, to: event.target.value })} />
    </div>}
  </div>;
}

export function RecordPagination({ page, pageCount, total, loading = false, onPageChange }: { page: number; pageCount: number; total?: number; loading?: boolean; onPageChange: (page: number) => void }) {
  const { t } = useLanguage();
  if (pageCount <= 1 && total === undefined) return null;
  return <nav className="catalog-pagination record-pagination" aria-label={t("记录分页", "Record pagination")}>
    <button type="button" disabled={page <= 1 || loading} onClick={() => onPageChange(Math.max(1, page - 1))}>{t("上一页", "Previous")}</button>
    <span>{total === undefined
      ? t(`第 ${page} / ${pageCount} 页`, `Page ${page} / ${pageCount}`)
      : t(`共 ${total} 条 · 第 ${page} / ${pageCount} 页`, `${total} records · Page ${page} / ${pageCount}`)}</span>
    <button type="button" disabled={page >= pageCount || loading} onClick={() => onPageChange(Math.min(pageCount, page + 1))}>{t("下一页", "Next")}</button>
  </nav>;
}

export function dateBounds(filter: DateFilterValue, now = new Date()): { from?: string; to?: string } {
  if (filter.mode === "ALL") return {};
  if (filter.mode === "CUSTOM") return { from: filter.from || undefined, to: filter.to || undefined };
  if (filter.mode === "WEEK") {
    const match = /^(\d{4})-W(\d{2})$/.exec(filter.from);
    if (!match) return {};
    const year = Number(match[1]);
    const week = Number(match[2]);
    const januaryFourth = new Date(year, 0, 4);
    const januaryFourthDay = januaryFourth.getDay() || 7;
    const from = new Date(year, 0, 4 - januaryFourthDay + 1 + (week - 1) * 7);
    const to = new Date(from.getFullYear(), from.getMonth(), from.getDate() + 6);
    return { from: localDate(from), to: localDate(to) };
  }
  if (filter.mode === "MONTH") {
    const match = /^(\d{4})-(\d{2})$/.exec(filter.from);
    if (!match) return {};
    const year = Number(match[1]);
    const month = Number(match[2]);
    return { from: localDate(new Date(year, month - 1, 1)), to: localDate(new Date(year, month, 0)) };
  }
  const year = Number(filter.from);
  if (!Number.isInteger(year) || year < 1) return {};
  return { from: `${year}-01-01`, to: `${year}-12-31` };
}

export function matchesRecordDate(value: string | null | undefined, filter: DateFilterValue): boolean {
  const bounds = dateBounds(filter);
  if (!bounds.from && !bounds.to) return true;
  if (!value) return false;
  const timestamp = new Date(value).getTime();
  if (!Number.isFinite(timestamp)) return false;
  if (bounds.from && timestamp < new Date(`${bounds.from}T00:00:00`).getTime()) return false;
  if (bounds.to) {
    const endExclusive = new Date(`${bounds.to}T00:00:00`);
    endExclusive.setDate(endExclusive.getDate() + 1);
    if (timestamp >= endExclusive.getTime()) return false;
  }
  return true;
}

export function pageSlice<T>(items: T[], page: number, pageSize: number): { items: T[]; page: number; pageCount: number } {
  const pageCount = Math.max(1, Math.ceil(items.length / pageSize));
  const safePage = Math.min(Math.max(1, page), pageCount);
  return { items: items.slice((safePage - 1) * pageSize, safePage * pageSize), page: safePage, pageCount };
}

function localDate(value: Date): string {
  const year = value.getFullYear();
  const month = String(value.getMonth() + 1).padStart(2, "0");
  const day = String(value.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

function defaultPeriod(mode: DateFilterMode, now = new Date()): string {
  if (mode === "MONTH") return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`;
  if (mode === "YEAR") return String(now.getFullYear());
  if (mode !== "WEEK") return "";
  const local = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const day = local.getDay() || 7;
  const thursday = new Date(local.getFullYear(), local.getMonth(), local.getDate() + 4 - day);
  const yearStart = new Date(thursday.getFullYear(), 0, 1);
  const week = Math.ceil((((thursday.getTime() - yearStart.getTime()) / 86_400_000) + 1) / 7);
  return `${thursday.getFullYear()}-W${String(week).padStart(2, "0")}`;
}
